/**
 * Unified localStorage / sessionStorage token readers.
 *
 * Historical divergence: role detection in the interview-room used
 * `vhire_admin_token || auth_token`, but `handleEndInterview` and several other
 * callsites read only `auth_token` — which breaks for admins authenticated
 * through the newer tenant-admin flow (they only have `vhire_admin_token`).
 * Route all interview-page auth reads through these helpers so they stay in
 * sync.
 */

export function getAdminToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('vhire_admin_token') || localStorage.getItem('auth_token');
}

/**
 * Candidate token, in priority order:
 *
 *   1. `candidate_session_token` (sessionStorage) — short-lived JWT minted
 *      by interview-room's joinWithSecret. Tab-scoped, ~4 h TTL. Set the
 *      moment a candidate redeems a magic link.
 *
 *   2. `vhire_token` (localStorage) — long-lived JWT minted by
 *      auth-service when a candidate signs in via /auth/login (OAuth or
 *      email magic-link). This is the actual key the rest of v4-frontend
 *      uses (`setAuthToken` in `api.ts`). Used to live out of sync from
 *      this helper, which caused the "/interview ↔ /auth/login" redirect
 *      loop: useAuth().isAuthenticated read vhire_token, getCandidateToken
 *      didn't, so /interview thought "no auth" and bounced to /auth/login,
 *      which thought "already authed" and bounced back. Keeping both
 *      readers pointed at the same key fixes that.
 *
 *   3. `candidate_token` (localStorage) — historical key. Some older
 *      callsites may still write here; kept as a fallback.
 */
export function getCandidateToken(): string | null {
  if (typeof window === 'undefined') return null;
  return (
    sessionStorage.getItem('candidate_session_token')
    || localStorage.getItem('vhire_token')
    || localStorage.getItem('candidate_token')
  );
}

/**
 * Token for interview-room admin actions (scores, notes, review page, etc).
 * Priority order:
 *   1. Tenant admin JWT (internal recruiter/interviewer)
 *   2. Room-scoped session JWT (external interviewer invited via magic link)
 *
 * The gateway's authenticateInterviewRoom() helper accepts either — tenant
 * admin gets tenant-wide access; external interviewer gets access only to
 * the room whose id is in their token's `scope` claim.
 */
export function getInterviewRoomAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  return (
    localStorage.getItem('vhire_admin_token') ||
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('candidate_session_token')
  );
}
